import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { Box, MenuItem, SelectChangeEvent } from '@mui/material';
import { styled as materialStyled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import {
  CommonTextField,
  CommonEnhancedDropDown,
  CommonErrorHelperTextV1,
  CommonButtonV1,
} from 'gemlux-common-components';
import Shapes from './Shapes';
import Colors from './Colors';
import { FilterData, ListType } from '../FilterModel';
import { colorFilters, getLocationArray, leftFilterFields, originAndLocationArray, rightFilterFields } from './Filters';
import { theme } from '../../../../theme/theme';
import { ISelectedFilterOption } from '../../../../redux/gem/gemSlice';
import { GemFeature } from '../../../../utils/enums/gem-feature.enum';
import { Titles } from '../../../../utils/general-layout-util';
import { filterScrollbarStyles } from '../../../../utils/styles';
import CloseIcon from '../../../../assets/img/V1/black-cross-icon.svg';
import { CustomDatePicker } from './CustomDatePicker';
import CustomSelectComponent from './CustomSelectComponent';
import { ImageSource } from '../../../../utils/enums/gem-image-source.enum';

export const CustomButton = materialStyled(Button)(({ selected }: { selected: boolean }) => ({
  borderRadius: '8px',
  padding: '4px 8px',
  textAlign: 'center',
  textTransform: 'none',
  fontFamily: 'Satoshi',
  fontSize: 15,
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
  height: '28px',
  color: theme.palette.black.main,
  border: selected
    ? `1px solid ${theme.palette.shades.primaryBrightDark}`
    : `1px solid ${theme.palette.shades.whiteBorder2}`,
  backgroundColor: selected ? theme.palette.shades.lightBrightBlue : 'transparent',
  '&:hover': {
    color: theme.palette.black.main,
    backgroundColor: selected ? theme.palette.secondary.light : 'transparent',
    border: `1px solid ${theme.palette.shades.primaryBrightDark}`,
  },
}));

export const CustomTypography = materialStyled(Typography)({
  color: theme.palette.error.main,
  fontSize: 12,
});

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: '8px',
  border: `1px solid ${theme.palette.shades.whiteBorder2}`,
  boxShadow: `0px 0px 24px 0px ${theme.palette.shades.shadow}`,
  width: '98.75%',
  outline: 'none',
};

const dimensions = ['Length', 'Width', 'Depth'];
const weights = ['min', 'max'];

interface DimensionsError {
  Length: boolean;
  Width: boolean;
  Depth: boolean;
}
interface DimensionsErrorMessage {
  Length: string;
  Width: string;
  Depth: string;
}
type IFilterModalProps = {
  onClose: () => void;
  open: boolean;
  onApply: (_filters: ISelectedFilterOption[]) => void;
  initialFilters: ISelectedFilterOption[];
  data?: FilterData;
  title?: Titles;
  handleCallList?: (_list_type: ListType) => void;
  loading?: { organization: boolean; user: boolean };
};

function GemFilterModal({
  onClose,
  open,
  onApply,
  initialFilters,
  data,
  title,
  handleCallList,
  loading,
}: IFilterModalProps) {
  const [selectedOptions, setSelectedOptions] = useState<ISelectedFilterOption[]>(initialFilters);
  const [openOwnerDropDown, setOpenOwnerDropDown] = useState(false);
  const [openOrganizationDropDown, setOpeOrganizationDropDown] = useState(false);

  const [dimensionsError, setDimensionsError] = useState<DimensionsError>({
    Length: false,
    Width: false,
    Depth: false,
  });
  const [dimensionErrorMessage, setDimensionErrorMessage] = useState<DimensionsErrorMessage>({
    Length: '',
    Width: '',
    Depth: '',
  });
  const [weightError, setWeightError] = useState<boolean>(false);
  const [weightErrorMessage, setWeightErrorMessage] = useState<string>('');
  const [hasInteractedWithFilters, setHasInteractedWithFilters] = useState(false);

  const isDisabled =
    (!hasInteractedWithFilters && !selectedOptions.length) ||
    Object.values(dimensionsError).some((error) => error) ||
    weightError;

  const handleSelectOption = (gemFeature: string, value: string) => {
    const newSelectedFilterOptions = [...selectedOptions];
    const featureIndex = newSelectedFilterOptions.findIndex((option) => Object.keys(option)[0] === gemFeature);

    if (featureIndex !== -1) {
      const existingValues = newSelectedFilterOptions[featureIndex][gemFeature] as string[];
      if (existingValues.includes(value)) {
        newSelectedFilterOptions[featureIndex] = {
          [gemFeature]: existingValues.filter((val) => val !== value),
        };

        if (newSelectedFilterOptions[featureIndex][gemFeature].length === 0) {
          newSelectedFilterOptions.splice(featureIndex, 1);
        }
      } else {
        newSelectedFilterOptions[featureIndex] = {
          [gemFeature]: [...existingValues, value],
        };
      }
    } else {
      newSelectedFilterOptions.push({ [gemFeature]: [value] });
    }
    setSelectedOptions(newSelectedFilterOptions);
  };

  const isSelected = (gemFeature: string, value: string): boolean => {
    const featureIndex = selectedOptions.findIndex((option) => Object.keys(option)[0] === gemFeature);
    if (featureIndex !== -1) {
      const existingValues = selectedOptions[featureIndex][gemFeature];
      return existingValues.includes(value);
    }
    return false;
  };

  const getValue = (gemFeature: string): string => {
    const featureIndex = selectedOptions.findIndex((option) => Object.keys(option)[0] === gemFeature);
    if (featureIndex !== -1) {
      const existingValues = selectedOptions[featureIndex][gemFeature];
      return existingValues as string;
    }
    return '';
  };

  const handleApplyFilters = () => {
    if (selectedOptions.length === 0) {
      setHasInteractedWithFilters(false);
    }
    onApply(selectedOptions);
  };

  const handleResetFilters = () => {
    setSelectedOptions([]);
    setDimensionsError({
      Length: false,
      Width: false,
      Depth: false,
    });
    setWeightError(false);
  };

  const handleChange = (gemFeature: string, value: string) => {
    const newSelectedFilterOptions = [...selectedOptions];
    const featureIndex = newSelectedFilterOptions.findIndex((option) => Object.keys(option)[0] === gemFeature);

    if (featureIndex !== -1) {
      if (!value) {
        newSelectedFilterOptions.splice(featureIndex, 1);
      } else {
        newSelectedFilterOptions[featureIndex] = {
          [gemFeature]: value,
        };
      }
    } else {
      newSelectedFilterOptions.push({ [gemFeature]: value });
    }
    setSelectedOptions(newSelectedFilterOptions);
  };

  const handleClose = (type: ListType) => {
    if (type === ListType.USER) {
      setOpenOwnerDropDown(false);
    } else {
      setOpeOrganizationDropDown(false);
    }
  };
  const handleOpen = (type: ListType) => {
    if (type === ListType.USER) {
      setOpenOwnerDropDown(true);
    } else {
      setOpeOrganizationDropDown(true);
    }
  };

  const validateDimensions = (dimension: keyof DimensionsError) => {
    const min = getValue(`min${dimension}`);
    const max = getValue(`max${dimension}`);

    const minValue = parseFloat(min);
    const maxValue = parseFloat(max);

    if (dimensionsError)
      setDimensionsError({
        ...dimensionsError,
        [dimension]: false,
      });
    setDimensionErrorMessage({
      ...dimensionErrorMessage,
      [dimension]: '',
    });

    if (minValue < 3 || maxValue < 3) {
      setDimensionsError({
        ...dimensionsError,
        [dimension]: true,
      });
      setDimensionErrorMessage({
        ...dimensionErrorMessage,
        [dimension]: `${dimension} cannot be less than 3mm.`,
      });
    }

    if (minValue > 25 || maxValue > 25) {
      setDimensionsError({
        ...dimensionsError,
        [dimension]: true,
      });
      setDimensionErrorMessage({
        ...dimensionErrorMessage,
        [dimension]: `${dimension} cannot exceed 25mm.`,
      });
    }

    if (minValue >= maxValue) {
      setDimensionsError({
        ...dimensionsError,
        [dimension]: true,
      });
      setDimensionErrorMessage({
        ...dimensionErrorMessage,
        [dimension]: 'Minimum value should be less than Maximum value.',
      });
    }
  };

  const validateWeight = () => {
    const minWeight = getValue('minWeight');
    const maxWeight = getValue('maxWeight');

    if (minWeight === '' || maxWeight === '') {
      setWeightError(false);
      setWeightErrorMessage('');
    }

    const minValue = parseFloat(minWeight);
    const maxValue = parseFloat(maxWeight);

    if (weightError) setWeightError(false);

    if (minValue < 0.5 || maxValue < 0.5) {
      setWeightError(true);
      setWeightErrorMessage('Weight cannot be less than 0.5ct.');
    }

    if (minValue > 20 || maxValue > 20) {
      setWeightError(true);
      setWeightErrorMessage('Weight cannot exceed 20 carats.');
    }

    if (minValue >= maxValue) {
      setWeightError(true);
      setWeightErrorMessage('Minimum value should be less than Maximum value.');
    }
  };

  useEffect(() => {
    validateDimensions('Length');
  }, [getValue('minLength'), getValue('maxLength')]);

  useEffect(() => {
    validateDimensions('Width');
  }, [getValue('minWidth'), getValue('maxWidth')]);

  useEffect(() => {
    validateDimensions('Depth');
  }, [getValue('minDepth'), getValue('maxDepth')]);

  useEffect(() => {
    validateWeight();
  }, [getValue('minWeight'), getValue('maxWeight')]);

  useEffect(() => {
    if (selectedOptions.length > 0 || weightError || Object.values(dimensionsError).some((error) => error)) {
      setHasInteractedWithFilters(true);
    }
  }, [selectedOptions, weightError, dimensionsError]);

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition data-testid="modal-content">
      <Grid container sx={{ ...modalStyle }}>
        <Grid display="flex" justifyContent="space-between" p={2} item xs={12}>
          {' '}
          <Typography variant="textH3" textAlign="left" color="text">
            Filters
          </Typography>
          <IconButton size="small" aria-label="close" onClick={onClose}>
            <Box component="img" src={CloseIcon} />
          </IconButton>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid container sx={{ height: '78vh', overflow: 'auto', ...filterScrollbarStyles }}>
          <Grid container>
            <Grid item xs={12} md={6} p={2} gap={2} display="flex" flexDirection="column">
              <Typography variant="textH6">Dimensions (mm)</Typography>
              <Grid display="flex" flexDirection="row" gap={1}>
                {dimensions.map((dimension) => (
                  <Grid display="flex" flexDirection="column" gap={0.5} key={dimension}>
                    <Typography variant="textBase500">{dimension}</Typography>
                    <Grid display="flex" flexDirection="row" gap={1}>
                      <CommonTextField
                        placeholder="Min"
                        type="number"
                        onChange={(event) => {
                          validateDimensions(dimension as keyof DimensionsError);
                          handleChange(`min${dimension}`, event.target.value);
                        }}
                        value={getValue(`min${dimension}`)}
                        inputProps={{ min: 0 }}
                        onKeyPress={(event) => {
                          if (event.key === '-' || event.key === 'e' || event.key === 'E') {
                            event.preventDefault();
                          }
                        }}
                        error={dimensionsError[dimension as keyof DimensionsError]}
                      />
                      <CommonTextField
                        placeholder="Max"
                        type="number"
                        onChange={(event) => {
                          validateDimensions(dimension as keyof DimensionsError);
                          handleChange(`max${dimension}`, event.target.value);
                        }}
                        inputProps={{ min: 0 }}
                        onKeyPress={(event) => {
                          if (event.key === '-' || event.key === 'e' || event.key === 'E') {
                            event.preventDefault();
                          }
                        }}
                        error={dimensionsError[dimension as keyof DimensionsError]}
                        value={getValue(`max${dimension}`)}
                      />
                    </Grid>
                    {dimensionsError[dimension as keyof DimensionsError] && (
                      <Grid item xs={12} sm={12}>
                        <CommonErrorHelperTextV1
                          errorText={dimensionErrorMessage[dimension as keyof DimensionsErrorMessage]}
                        />
                      </Grid>
                    )}
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid item xs={12} md={6} p={2} gap={2} display="flex" flexDirection="column">
              <Typography variant="textH6">Weight (ct)</Typography>
              <Grid display="flex" flexDirection="row" gap={1}>
                {weights.map((weight) => (
                  <Grid display="flex" flexDirection="column" gap={0.5} key={weight} width="100%">
                    <Typography variant="textBase500" textTransform="capitalize">
                      {weight}
                    </Typography>
                    <Grid display="flex" flexDirection="row" gap={1}>
                      <CommonTextField
                        placeholder="0.00"
                        type="number"
                        fullWidth
                        inputProps={{ min: 0 }}
                        onKeyPress={(event) => {
                          if (event.key === '-' || event.key === 'e' || event.key === 'E') {
                            event.preventDefault();
                          }
                        }}
                        onChange={(event) => handleChange(`${weight}Weight`, event.target.value)}
                        value={getValue(`${weight}Weight`)}
                        error={weightError}
                      />
                    </Grid>
                  </Grid>
                ))}
              </Grid>
              {weightError && (
                <Grid item xs={12} sm={12}>
                  <CommonErrorHelperTextV1 errorText={weightErrorMessage} />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid container>
            {colorFilters.map((filter) => (
              <Grid item xs={12} sm={6} p={2} key={filter.title}>
                <Grid mb={1}>
                  <Typography variant="textH6" textTransform="capitalize">
                    {filter.title}
                  </Typography>{' '}
                </Grid>

                <Grid display="flex" gap={1} item flexWrap="wrap" data-testid={filter.testId}>
                  {[GemFeature.COLOR, GemFeature.SECONDARY_COLOR].includes(filter.title) ? (
                    <Colors handleSelectOption={handleSelectOption} isSelected={isSelected} type={filter.code} />
                  ) : (
                    filter.options.map((option) => (
                      <CustomButton
                        key={option}
                        variant="outlined"
                        onClick={() => handleSelectOption(filter.code, option)}
                        selected={isSelected(filter.code, option)}
                      >
                        <Typography variant="textBase500">{option}</Typography>
                      </CustomButton>
                    ))
                  )}
                </Grid>
              </Grid>
            ))}
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid container>
            <Grid item xs={12} sm={6}>
              {leftFilterFields.map((filter) => (
                <Grid p={2} key={filter.title} gap={1} display="flex" flexDirection="column">
                  <Typography variant="textH6" textTransform="capitalize">
                    {filter.title}
                  </Typography>
                  <Grid display="flex" gap={1} item flexWrap="wrap">
                    {filter.options.map((option) => (
                      <CustomButton
                        key={option}
                        variant="outlined"
                        onClick={() => handleSelectOption(filter.code, option)}
                        selected={isSelected(filter.code, option)}
                      >
                        <Typography variant="textBase500">{option}</Typography>
                      </CustomButton>
                    ))}
                  </Grid>
                </Grid>
              ))}
            </Grid>

            <Grid item xs={12} sm={6}>
              {rightFilterFields.map((filter) =>
                filter.title === GemFeature.SHAPE ? (
                  <Grid p={2} key={filter.title} gap={1} display="flex" flexDirection="column">
                    <Typography variant="textH6" textTransform="capitalize">
                      {filter.title}
                    </Typography>
                    <Grid display="flex" gap={1} item flexWrap="wrap">
                      <Shapes handleSelectOption={handleSelectOption} isSelected={isSelected} />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid p={2} key={filter.title} gap={1} display="flex" flexDirection="column">
                    <Typography variant="textH6" textTransform="capitalize">
                      {filter.title}
                    </Typography>
                    <Grid display="flex" gap={1} item flexWrap="wrap">
                      {filter.options.map((option) => (
                        <CustomButton
                          key={option}
                          variant="outlined"
                          onClick={() => handleSelectOption(filter.code, option)}
                          selected={isSelected(filter.code, option)}
                        >
                          <Typography variant="textBase500">{option}</Typography>
                        </CustomButton>
                      ))}
                    </Grid>
                  </Grid>
                ),
              )}
            </Grid>
          </Grid>
          <Grid container p={2} spacing={2}>
            <Grid item xs={12} md={12}>
              <Typography variant="textH6">Listing Date</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={1}>
                {' '}
                <Grid item xs={12} md={6}>
                  <Typography variant="textBase500">From</Typography>
                  <CustomDatePicker
                    onChange={(newValue) =>
                      handleChange('fromDate', newValue ? dayjs(newValue).format('YYYY-MM-DD') : '')
                    }
                    value={getValue('fromDate') ? dayjs(getValue('fromDate')) : null}
                    maxDate={dayjs(getValue('toDate'))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="textBase500">To</Typography>
                  <CustomDatePicker
                    onChange={(newValue) =>
                      handleChange('toDate', newValue ? dayjs(newValue).format('YYYY-MM-DD') : '')
                    }
                    value={getValue('toDate') ? dayjs(getValue('toDate')) : null}
                    minDate={dayjs(getValue('fromDate'))}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6} pt={1} gap={2} display="flex" flexDirection="row">
              <Grid item xs={12} md={6} data-testid="origin-select">
                <Typography variant="textH6">Origin</Typography>
                <CustomSelectComponent
                  fullWidth
                  defaultValue=""
                  value={getValue('origin')}
                  onChange={(event: SelectChangeEvent<unknown>) => handleChange('origin', event.target.value as string)}
                >
                  {originAndLocationArray.map((item) => (
                    <MenuItem key={item.origin} value={item.origin}>
                      {item.origin}
                    </MenuItem>
                  ))}
                </CustomSelectComponent>
              </Grid>

              <Grid item xs={12} md={6} data-testid="location-select">
                <Typography variant="textH6">Location</Typography>

                <CustomSelectComponent
                  fullWidth
                  defaultValue=""
                  value={getValue('location')}
                  onChange={(event: SelectChangeEvent<unknown>) =>
                    handleChange('location', event.target.value as string)
                  }
                >
                  {getLocationArray(getValue('origin')).map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </CustomSelectComponent>
              </Grid>
            </Grid>
          </Grid>
          {(title === Titles.GemBank || title === Titles.MyGemFav) && (
            <Grid container>
              <Grid item xs={12} md={3} p={2} gap={1} display="flex" flexDirection="column">
                <Grid item>
                  <Typography variant="textH6">Owner</Typography>
                </Grid>
                <Grid item>
                  <CommonEnhancedDropDown
                    open={openOwnerDropDown}
                    placeholder="Select Owner"
                    ariaLabelledBy="Owner"
                    options={data?.owner ?? []}
                    name="Owner"
                    onBlur={() => {}}
                    onChange={(event: SelectChangeEvent<unknown>) => handleChange('user', event.target.value as string)}
                    value={getValue('user')}
                    onFocus={() => {
                      if (handleCallList) {
                        handleCallList(ListType.USER);
                      }
                    }}
                    handleOpen={() => {
                      handleOpen(ListType.USER);
                    }}
                    handleClose={() => {
                      handleClose(ListType.USER);
                    }}
                    isLoading={loading?.user ?? false}
                  />
                </Grid>
              </Grid>

              <Grid item xs={12} md={3} p={2} gap={1} display="flex" flexDirection="column">
                <Grid item>
                  <Typography variant="textH6">Organization</Typography>
                </Grid>
                <Grid item>
                  <CommonEnhancedDropDown
                    open={openOrganizationDropDown}
                    placeholder="Select Organization"
                    ariaLabelledBy="Organization"
                    options={data?.organization ?? []}
                    name="Organisation"
                    onBlur={() => {}}
                    onChange={(event: SelectChangeEvent<unknown>) =>
                      handleChange('organisation', event.target.value as string)
                    }
                    value={getValue('organisation')}
                    onFocus={() => {
                      if (handleCallList) {
                        handleCallList(ListType.ORGANIZATION);
                      }
                    }}
                    handleOpen={() => {
                      handleOpen(ListType.ORGANIZATION);
                    }}
                    handleClose={() => {
                      handleClose(ListType.ORGANIZATION);
                    }}
                    isLoading={loading?.organization ?? false}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <Grid p={2} gap={1} display="flex" flexDirection="column">
              <Typography variant="textH6" textTransform="capitalize">
                Image Source
              </Typography>
              <Grid display="flex" gap={1} item flexWrap="wrap">
                <CustomButton
                  key="ownDevice"
                  variant="outlined"
                  onClick={() => handleSelectOption('isScanned', ImageSource.OwnDevice)}
                  selected={isSelected('isScanned', ImageSource.OwnDevice)}
                >
                  <Typography variant="textBase500">Own Device</Typography>
                </CustomButton>
                <CustomButton
                  key="kristaKroma"
                  variant="outlined"
                  onClick={() => handleSelectOption('isScanned', ImageSource.KristaKroma)}
                  selected={isSelected('isScanned', ImageSource.KristaKroma)}
                >
                  <Typography variant="textBase500">KRISTA™/KROMA™</Typography>
                </CustomButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid container p="24px 16px 16px 16px" gap={3} display="flex" justifyContent="flex-end">
          <CommonButtonV1
            padding="12px 56px"
            variant="outlined"
            disabled={!selectedOptions.length}
            onClick={handleResetFilters}
            backgroundColor="highlightText"
            sx={{
              border: `1px solid ${theme.palette.grey[300]}`,
              color: theme.palette.black.main,
              '&.Mui-disabled': {
                backgroundColor: 'transparent',
                boxShadow: '',
                cursor: 'not-allowed',
                color: theme.palette.shades.disabled,
                border: `1px solid ${theme.palette.shades.disabled}`,
              },
            }}
          >
            <Typography variant="textBase500">Reset Filters</Typography>
          </CommonButtonV1>
          <CommonButtonV1
            padding="12px 56px"
            variant="contained"
            disabled={isDisabled}
            onClick={handleApplyFilters}
            sx={{
              border: `1px solid ${theme.palette.grey[300]}`,
            }}
          >
            <Typography variant="textBase500">Apply Filters</Typography>
          </CommonButtonV1>
        </Grid>
      </Grid>
    </Modal>
  );
}

export default GemFilterModal;
